<template>
    <div class="project-page-container">

        <!-- Hero -->
        <div class="project-hero">
            <h1 class="project-h1">Plan Library &amp; Community Management</h1>
            <img class="project-hero-img dashboard-hero-img" src="../assets/plan-lib-palette-preview.png" alt="Quote Dashboard">
            <div class="project-hero-subtext">
                <p class="project-contribution">Enterprise UX Design</p>
                <p class="project-contribution">User Research</p>
                <p class="project-contribution">Cross-Product Ecosystem Design</p>
            </div>
        </div>

        <!-- Description -->
        <div class="project-background light-green">
            <div class="project-background-inner-container">
                <h2 class="project-background-h2 project-description-h2">
                    At Paradigm, I designed the Plan Library and Community Management features inside myBLDR, an enterprise platform used by homebuilders and sales reps.
                    <br><br>
                    This feature gives builders a self-service path to upload a blueprint set, generate an AI-powered 3D preview, and apply their material palette without relying on rep outreach at every step. The result was a measurable reduction in setup time and a meaningful lift in downstream product adoption.
                </h2>
            </div>
        </div>

        <!-- Business Impact -->
        <div class="project-background">
            <div class="project-background-inner-container">
                <h2 class="project-background-h2">End Result &amp; Business Impact</h2>
                <div class="business-impact">
                    <div class="business-impact-imgs">
                        <img class="dashboard-web-mockup" src="../assets/plan-lib-community.png" alt="myBLDR community dashboard showing lot map, metrics, and master plan thumbnails">
                    </div>
                    <div class="business-impact-text">
                        <div>
                            <div>&bull;</div>
                            <div>Builders went from a multi-day, rep-assisted plan setup process to completing it in a single self-service session.</div>
                        </div>
                        <div>
                            <div>&bull;</div>
                            <div>Home Configure conversion increased significantly among builders who completed the AI render and palette preview, validating the value of connecting the two products directly.</div>
                        </div>
                        <div>
                            <div>&bull;</div>
                            <div>Sales rep intervention dropped noticeably for plan setup tasks, freeing reps to focus on selling rather than configuration support.</div>
                        </div>
                        <div>
                            <div>&bull;</div>
                            <div>The palette tool saw strong first-session engagement once builders could see their render, confirming that sequencing the preview before configuration was the right call.</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Ecosystem Context -->
        <div class="project-background-inner-container">
            <h2 class="project-background-h2">Understanding the Ecosystem</h2>
            <p class="project-background-p">
                Before designing a single screen, I mapped how the Plan Library fit within myBLDR and the broader Paradigm suite. A builder uploads their blueprint in myBLDR, AI parses it and generates a 3D model in Home Configure, the builder applies their material palette, and those selections flow through Paradigm Estimate where estimators build cost and material lists. Those lists return to myBLDR where sales reps polish and publish them, and the builder orders materials for delivery to the job site. Designing without understanding those dependencies would have produced something that looked right but broke the handoff at every seam.
            </p>
            <div class="project-img-container" style="margin-top: 24px;">
                <img class="dashboard-visual-sample" style="border: 1px solid #e0e0e0;" src="../assets/plan-library-ecosystem.svg" alt="Diagram showing Plan Library connecting myBLDR, Home Configure, Paradigm Estimate, and the sales rep workflow into one continuous pipeline">
                <p class="caption-p">Plan Library is the entry point of a plan-to-delivery workflow spanning four products. Every touchpoint needed to be aligned so builders could move from blueprint to job site without leaving the suite.</p>
            </div>
        </div>

        <!-- Problem -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Problem</h2>
            <p class="project-background-p problem-p">
                Builders and sales reps were losing time at every step because the products were not interconnected and relied heavily on rep-driven outreach to move forward.
            </p>
            <div class="business-impact">
                <div class="business-impact-text">
                    <div>
                        <div>&bull;</div>
                        <div>Uploading a plan and getting to a viewable 3D preview required multiple back-and-forth emails with a rep, often stretching across several days.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div>Builders had no self-service way to apply their own material palette to a render, so visualizing the finished home depended entirely on rep availability.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div>Without a shared community view, builders and reps had no single place to track lot status, active builds, or occupancy across a development.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div>Deals stalled when builders could not see their product clearly. The gap between uploading a plan and understanding what could be built was too wide.</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Goals and Deliverables -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Goals &amp; Deliverables</h2>
            <p class="project-background-p">
                1. <span class="bold-span">Plan Upload and AI Preview:</span> A self-service flow where builders upload a blueprint set, AI parses it automatically, and a 3D render is available in the same session with no rep involvement required.
                <br><br>
                2. <span class="bold-span">Palette Selection:</span> An interface for builders to apply their own material and color palette to the AI render in real time, including support for uploading a look book to see actual product selections on the preview.
                <br><br>
                3. <span class="bold-span">Plan Library Browsing:</span> A filterable grid of all active plan models, with range-based filters for width, depth, square footage, beds, and baths that match how builders think about plans rather than how the database stores them.
                <br><br>
                4. <span class="bold-span">Community Management:</span> A project-level view with a lot map, real-time status coding, and the four community metrics that builders and reps identified as most important in discovery interviews.
            </p>
        </div>

        <!-- Design Process -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Design Process</h2>
            <div class="business-impact">
                <div class="business-impact-text">
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Discovery:</span> Conducted user interviews with builders and sales reps to map pain points, mental models, and daily workflow patterns. Builder feedback was direct: they wanted to upload a plan and immediately see what the house looked like without emailing anyone to make it happen. Those findings shaped the KPI framework and experience requirements before any screens were touched.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Cross-functional alignment:</span> Facilitated working sessions with product and engineering to validate requirements against technical constraints across multiple products. Aligning myBLDR, Home Configure, and Paradigm Estimate early prevented design decisions that look reasonable in isolation but break the handoff in practice.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Wireframes to prototypes:</span> Iterated from low-fidelity wireframes through interactive Figma prototypes. A key finding from prototype testing was that presenting palette configuration before the 3D render caused significant drop-off. Builders wanted to see the AI output first and then be invited to customize it. Reversing that sequence meaningfully improved engagement in follow-up sessions.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Accessibility throughout:</span> Designed with WCAG 2.1 AA contrast ratios, explicit form labels rather than placeholder-only text, and keyboard-navigable palette swatches and lot map interactions. Lot status on the community map uses both color and pattern so the coding is never color-only. Accessibility was a design constraint from the start, not a checklist at the end.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">User testing and iteration:</span> Validated designs with builders and reps through moderated sessions. Refined the community stat cards based on builder and rep feedback: Selection Progress was added to the lot summary card after reps said they needed to know a buyer's stage at a glance to determine follow-up priority.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Handoff and implementation support:</span> Delivered annotated specs and component documentation in Figma. Stayed engaged during development to clarify design intent and catch implementation drift before it shipped.</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Visual Samples -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Visual Samples</h2>

            <div class="project-img-container" style="margin-top: 20px;">
                <img class="dashboard-visual-sample" src="../assets/plan-lib-empty-plan.png" alt="Plan overview empty state with metadata fields before a plan set is uploaded">
                <p class="caption-p">The plan overview before a plan set is attached. Metadata fields give builders full control over how a plan is categorized across communities and series.</p>
            </div>

            <div class="project-img-container">
                <img class="dashboard-visual-sample" src="../assets/plan-lib-plan-exists.png" alt="Plan overview after upload showing the AI-generated 3D preview prompt and palette upload option">
                <p class="caption-p">After uploading a plan set, builders can view their plans in 3D and upload a look book to preview palette selections against the render.</p>
            </div>

            <div class="project-img-container">
                <img class="dashboard-visual-sample" src="../assets/plan-lib-palette-preview.png" alt="AI Generated Preview with palette chooser overlaid on the rendered home">
                <p class="caption-p">The palette chooser overlaid on the AI render. Builders switch between presets or create a custom palette that updates the render in real time.</p>
            </div>

            <div class="project-img-container">
                <img class="dashboard-visual-sample" src="../assets/plan-lib-select-color-palette.png" alt="Custom palette builder with material categories on the left and swatch options on the right">
                <p class="caption-p">The custom palette interface. Each category maps to how builders specify materials: siding, roofing, masonry, trim, front door, and more.</p>
            </div>

            <div class="project-img-container">
                <img class="dashboard-visual-sample" src="../assets/plan-lib-filters.png" alt="Plan Library grid view with filter panel open showing range filters for width, depth, square footage, beds, and baths">
                <p class="caption-p">The Plan Library filter panel. Range selectors reflect how builders think about plans by lot fit and buyer segment rather than exact database values.</p>
            </div>

            <div class="project-img-container">
                <img class="dashboard-visual-sample" src="../assets/plan-lib-community.png" alt="Silver Lake community dashboard showing metrics, master plan thumbnails, and an interactive lot map">
                <p class="caption-p">The community overview. Stat cards surface the four metrics builders and reps identified in discovery interviews. The lot map shows real-time status across every lot in the development.</p>
            </div>
        </div>

        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Thanks for reading!</h2>
        </div>
        <div class="project-background-inner-container not-first-cream-background back-to-top-container">
            <a @click="scrollToTop">Back to top</a>
            <router-link to="/projects">More projects</router-link>
        </div>

    </div>
</template>

<script>
export default {
    name: 'PlanLibraryProject',
    data: function() {
        return {};
    },
    methods: {
        scrollToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }
}
</script>